import { ChangeDetectionStrategy, Component, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { COURSE_INFO, SPRINTS } from '../data/course-data';

@Component({
  selector: 'app-hero-section',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="relative bg-blueprint-grid text-white py-16 px-4 sm:px-6 lg:px-8 border-b border-white/10 overflow-hidden">
      <!-- Ambient light blur overlay -->
      <div class="absolute -top-24 -left-24 w-96 h-96 bg-[#F27D26]/10 rounded-full blur-3xl pointer-events-none"></div>
      <div class="absolute -bottom-24 -right-24 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div class="max-w-7xl mx-auto">
        <!-- Main Title & Quote Banner -->
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
          <div class="lg:col-span-7 space-y-6">
            <h1 class="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
              Empreendedorismo <br/>
              <span class="text-[#F27D26] font-serif italic">
                em Redes
              </span>
            </h1>

            <p class="text-lg sm:text-xl text-zinc-300 font-normal leading-relaxed">
              Componente Curricular: Empreendedorismo (Carga Horária: 40h)
            </p>

            <p class="text-sm text-zinc-400 max-w-2xl leading-relaxed">
              Uma jornada prática de 40 horas onde a engenharia de infraestrutura encontra a criação de novos negócios sustentáveis em TIC. Do diagnóstico de problemas em conectividade até o deploy do modelo no mercado.
            </p>

            <!-- Quick Action Buttons -->
            <div class="flex flex-wrap gap-3 pt-2">
              <button 
                type="button"
                (click)="navigate.emit('aula1')"
                class="px-5 py-3 rounded-xl bg-[#F27D26] hover:bg-[#d86b1c] text-black font-bold text-sm shadow-lg shadow-[#F27D26]/25 transition-all flex items-center space-x-2 cursor-pointer">
                <span class="material-symbols-outlined">play_circle</span>
                <span>Iniciar Aula 1 & Dinâmica</span>
              </button>

              <button 
                type="button"
                (click)="navigate.emit('sprint4')"
                class="px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white border border-white/10 font-semibold text-sm transition-all flex items-center space-x-2 cursor-pointer">
                <span class="material-symbols-outlined">account_tree</span>
                <span>Business Model Canvas</span>
              </button>

              <button 
                type="button"
                (click)="navigate.emit('sprint1')"
                class="px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white border border-white/10 font-semibold text-sm transition-all flex items-center space-x-2 cursor-pointer">
                <span class="material-symbols-outlined">explore</span>
                <span>Explorar Sprints</span>
              </button>
            </div>
          </div>

          <!-- Quote Card -->
          <div class="lg:col-span-5">
            <div class="relative bg-[#121214] rounded-2xl border border-white/10 p-6 sm:p-8 shadow-2xl relative group">
              <!-- Corner Tech Decorative Accents -->
              <div class="absolute -top-1.5 -left-1.5 w-3 h-3 border-t-2 border-l-2 border-[#F27D26]"></div>
              <div class="absolute -top-1.5 -right-1.5 w-3 h-3 border-t-2 border-r-2 border-[#F27D26]"></div>
              <div class="absolute -bottom-1.5 -left-1.5 w-3 h-3 border-b-2 border-l-2 border-[#F27D26]"></div>
              <div class="absolute -bottom-1.5 -right-1.5 w-3 h-3 border-b-2 border-r-2 border-[#F27D26]"></div>

              <div class="flex items-center space-x-3 mb-4">
                <span class="material-symbols-outlined text-[#F27D26] text-3xl">format_quote</span>
                <span class="text-xs font-mono uppercase tracking-wider text-zinc-400">Princípio Chave</span>
              </div>

              <blockquote class="text-xl font-bold text-white mb-6 leading-snug font-serif italic">
                "{{ info.heroQuote.quote }}"
              </blockquote>

              <div class="grid grid-cols-2 gap-4 text-xs pt-4 border-t border-white/10">
                <div class="p-3 rounded-xl bg-white/[0.03] border border-white/10">
                  <div class="font-bold text-[#F27D26] mb-1 flex items-center space-x-1">
                    <span class="material-symbols-outlined text-sm">dns</span>
                    <span>Habilidades Técnicas</span>
                  </div>
                  <p class="text-zinc-400">Constroem a solução e mantêm a rede no ar.</p>
                </div>

                <div class="p-3 rounded-xl bg-white/[0.03] border border-white/10">
                  <div class="font-bold text-amber-400 mb-1 flex items-center space-x-1">
                    <span class="material-symbols-outlined text-sm">lightbulb</span>
                    <span>Habilidades Empreendedoras</span>
                  </div>
                  <p class="text-zinc-400">Garantem que alguém queira usá-la e pagar por ela.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- O Mapa da Disciplina -->
        <div class="bg-[#121214] rounded-2xl border border-white/10 p-6 sm:p-8">
          <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 pb-4 border-b border-white/10">
            <div>
              <h2 class="text-2xl font-bold text-white flex items-center space-x-2">
                <span class="material-symbols-outlined text-[#F27D26]">route</span>
                <span>O Mapa da Disciplina</span>
              </h2>
              <p class="text-sm text-zinc-400 mt-1">40 Horas divididas em 4 Sprints de 10 Horas com foco em inovação em TIC.</p>
            </div>
          </div>

          <!-- Sprints Timeline Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            @for (sprint of sprints; track sprint.id) {
              <button 
                type="button"
                (click)="navigate.emit('sprint' + sprint.id)"
                class="p-5 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/10 hover:border-[#F27D26]/50 transition-all cursor-pointer group text-left flex flex-col justify-between">
                <div>
                  <div class="flex items-center justify-between mb-3">
                    <div class="w-10 h-10 rounded-lg flex items-center justify-center font-bold"
                      [style.background-color]="sprint.color + '20'"
                      [style.color]="sprint.color">
                      <span class="material-symbols-outlined text-2xl">{{ sprint.icon }}</span>
                    </div>
                    <span class="text-xs font-mono px-2 py-0.5 rounded bg-black/40 text-zinc-400 border border-white/10">
                      {{ sprint.hours }}h
                    </span>
                  </div>
                  <h3 class="font-bold text-white text-base group-hover:text-[#F27D26] transition-colors">
                    {{ sprint.title }}
                  </h3>
                  <p class="text-xs text-[#F27D26] font-medium mt-0.5 mb-2">{{ sprint.subtitle }}</p>
                  <p class="text-xs text-zinc-400 line-clamp-3 leading-relaxed">{{ sprint.description }}</p>
                </div>

                <div class="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-xs text-[#F27D26] font-medium">
                  <span>Ver Sprint</span>
                  <span class="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform">arrow_forward</span>
                </div>
              </button>
            }
          </div>
        </div>
      </div>
    </section>
  `
})
export class HeroSectionComponent {
  info = COURSE_INFO;
  sprints = SPRINTS;
  navigate = output<string>();
}
